import * as React from 'react';
import {
  Box,
  Icon,
  StackLayout,
  TextAction,
} from '@leagueplatform/genesis-core';
import { Link } from 'react-router-dom';

export interface BannerLinkItemProps {
  /**
   * Message text shown to the user. Always rendered. When `path` is
   * provided, the entire text is wrapped in a link to the offending
   * field; otherwise it renders as plain text.
   */
  detail: string;
  /**
   * Pre-resolved navigable form path to surface as `?path=<path>`. When
   * `undefined`, the message renders as plain text — i.e. either no
   * pointer was provided, or the upstream resolver intentionally
   * dropped it.
   */
  path?: string;
  /**
   * Optional click handler. When supplied, runs in addition to the
   * router-driven navigation — the error banner uses this to switch
   * locale before navigating to a field in a non-default locale.
   */
  onClick?: (event: React.MouseEvent) => void;
  /**
   * Optional `state` forwarded to react-router's `Link`, so navigation
   * preserves the current location state across the redirect (the
   * error banner uses this to keep the side panel open).
   */
  state?: unknown;
  /**
   * Optional content rendered directly under the link/text — e.g. the
   * error banner's secondary detail row with the copy button. Kept as a
   * children slot rather than a typed prop so callers can compose any
   * tree beneath the message without this component growing
   * banner-specific knowledge.
   */
  children?: React.ReactNode;
}

/**
 * Renders a banner message that, when accompanied by a navigable form
 * path, surfaces as a link to the offending field. Used by both the
 * error banner and the warning banner so the two stay visually
 * consistent — same typography, same arrow icon, same wrapper cascade.
 *
 * The component owns its own wrapping (`<Box><StackLayout>`) so callers
 * don't have to reproduce the structural pieces that previously caused
 * subtle visual divergence between the error and warning renderings
 * (the warning banner used to wrap `TextAction` directly inside
 * `ParagraphText as="li"`, which let the parent paragraph's typography
 * cascade through and made the link render with link-color rather than
 * `$onSurfaceTextPrimary`).
 *
 * Callers that need additional content underneath (e.g. a secondary
 * detail row) should pass it as `children`; it'll be rendered inside
 * the same outer `<Box>` so list-item alignment is consistent.
 */
export const BannerLinkItem: React.FC<BannerLinkItemProps> = ({
  detail,
  path,
  onClick,
  state,
  children,
}) => (
  <Box>
    <StackLayout orientation="horizontal" verticalAlignment="center">
      {path ? (
        <TextAction
          as={Link}
          to={`?path=${path}`}
          onClick={onClick}
          state={state}
          css={{
            typography: '$bodyOne',
            color: '$onSurfaceTextPrimary',
            textDecoration: 'none',
            '&:hover, &:focus-visible': {
              textDecoration: 'underline',
              textUnderlineOffset: '0.125rem',
            },
          }}
        >
          {detail}
          <Icon
            icon="interfaceArrowRight"
            size="$threeQuarters"
            tint="$interactiveActionPrimary"
            css={{
              marginInlineStart: '$half',
              verticalAlign: 'middle',
              flexShrink: 0,
              minWidth: '$threeQuarters',
              minHeight: '$threeQuarters',
            }}
          />
        </TextAction>
      ) : (
        detail
      )}
    </StackLayout>
    {children}
  </Box>
);
